import { Card } from "../UI/Card.tsx";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../UI/Layout/Layout.tsx";
import { ScreenTitleBlock } from "../UI/Layout/ScreenTitleBlock.tsx";
import { WeeklyCalendar } from "../Calendar/WeeklyCalendar.tsx";
import { ArrowLongLeftIcon } from "@heroicons/react/24/solid/index.js";
import { AllFranchiseeScreenPath } from "../Franchisee/FranchiseeScreen.tsx";
import { createTodayInterval } from "../Calendar/createTodayInterval.ts";
import { Interval } from "../Calendar/Interval.tsx";
import { useBookedClassRooms } from "./useBookedClassRooms.ts";
import { createBookedClassRoomsCalendarMap } from "./createBookedClassRoomsCalendarMap.ts";
import { BookedClassRoomSlotItem } from "./BookedClassRoomSlotItem.tsx";
import { SlotDetailPanel } from "./SlotDetailPanel.tsx";
import { BookedSlotGroup } from "./types.ts";
import { useAllFranchisees } from "../Franchisee/useAllFranchisees.ts";
import { FranchiseeItemFragment } from "../gql/graphql.ts";
import { useTeacherCandidateClassRooms } from "./useTeacherCandidateClassRooms.ts";
import { useAvailableTeacherSlots } from "./useAvailableTeacherSlots.ts";
import { BookedClassRoomsLegend } from "./BookedClassRoomsLegend.tsx";
import { countTotalsOfDay, countTotalsOfDays } from "./bookedSlotTotals.ts";
import { TeacherSlotFilter } from "./getTeacherSlotStats.ts";
import { Select, SelectOption } from "@normasteaching/norma-ui";
import { ExportBookedClassRoomsCsvModal } from "./ExportBookedClassRoomsCsvModal.tsx";
import { ExportCsvButton } from "../UI/ExportCsvButton.tsx";
import { TeacherWeeklyCapSummary } from "./TeacherWeeklyCapSummary.tsx";

export const BookedClassRoomsScreenPath = "/booked-classrooms";

export function BookedClassRoomsScreen() {
  const navigate = useNavigate();
  const [interval, setInterval] = useState<Interval>(createTodayInterval());
  const [selectedGroup, setSelectedGroup] = useState<BookedSlotGroup | null>(
    null,
  );
  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(
    null,
  );
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const { classRooms, loading } = useBookedClassRooms({
    fromDate: interval.fromDate,
    toDate: interval.toDate,
  });

  const { availableSlots, loading: loadingAvailability } =
    useAvailableTeacherSlots({
      fromDate: interval.fromDate,
      toDate: interval.toDate,
    });

  const { franchisees } = useAllFranchisees();
  const teacherById = new Map<string, FranchiseeItemFragment>(
    franchisees.map((f) => [f.id, f]),
  );

  const teacherOptions = useMemo<SelectOption<string>[]>(
    () =>
      franchisees
        .map((f) => ({ id: f.id, name: `${f.givenName} ${f.familyName}` }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [franchisees],
  );

  const {
    fetchCandidateRooms,
    candidateClassRoomIds,
    loading: loadingCandidates,
  } = useTeacherCandidateClassRooms();

  useEffect(() => {
    if (!selectedTeacherId || classRooms.length === 0) return;
    fetchCandidateRooms(
      selectedTeacherId,
      classRooms.map((c) => c.classRoomId),
    );
  }, [selectedTeacherId, classRooms, fetchCandidateRooms]);

  const filteredClassRooms = useMemo(() => {
    if (!selectedTeacherId) return classRooms;
    return classRooms.filter(
      (c) =>
        c.teacherId === selectedTeacherId ||
        candidateClassRoomIds.has(c.classRoomId),
    );
  }, [classRooms, selectedTeacherId, candidateClassRoomIds]);

  const filteredAvailableSlots = useMemo(() => {
    if (!selectedTeacherId) return availableSlots;
    return availableSlots
      .filter((s) => s.teacherIds.includes(selectedTeacherId))
      .map((s) => ({ ...s, teacherIds: [selectedTeacherId] }));
  }, [availableSlots, selectedTeacherId]);

  const items = useMemo(
    () =>
      createBookedClassRoomsCalendarMap(
        filteredClassRooms,
        filteredAvailableSlots,
      ),
    [filteredClassRooms, filteredAvailableSlots],
  );

  const totalsTeacherFilter = useMemo<TeacherSlotFilter | undefined>(
    () =>
      selectedTeacherId
        ? { teacherId: selectedTeacherId, candidateClassRoomIds }
        : undefined,
    [selectedTeacherId, candidateClassRoomIds],
  );

  const weekTotals = useMemo(
    () => countTotalsOfDays(items, interval.getDays(), totalsTeacherFilter),
    [items, interval, totalsTeacherFilter],
  );

  return (
    <Layout maxWClass={"max-w-full"}>
      <div
        onClick={() => navigate(AllFranchiseeScreenPath)}
        className={
          "mb-4 text-sm space-x-2 flex items-center text-slate-500 cursor-pointer"
        }
      >
        <ArrowLongLeftIcon className={"w-5"} />
        <p>Back to teacher list</p>
      </div>

      <div className={"mb-4"}>
        <Card>
          <ScreenTitleBlock
            title={"Booked Classrooms"}
            action={
              <ExportCsvButton onClick={() => setIsExportModalOpen(true)} />
            }
          />
        </Card>
      </div>

      {selectedTeacherId && (
        <div className={"mb-4"}>
          <Card>
            <TeacherWeeklyCapSummary
              teacherId={selectedTeacherId}
              weekStart={interval.fromDate}
            />
          </Card>
        </div>
      )}

      <WeeklyCalendar
        interval={interval}
        items={items}
        loading={loading}
        onChangeInterval={(i) => setInterval(i)}
        canGoToPrevWeek={true}
        periodSummary={
          <div
            className={
              "flex flex-col leading-tight text-xs text-slate-500 tabular-nums"
            }
          >
            <span className={"font-medium text-slate-600"}>
              {weekTotals.classRoomCount} classrooms
            </span>
            <span>
              {loadingAvailability
                ? "Loading availability…"
                : `${weekTotals.availableCount} available`}
            </span>
          </div>
        }
        daySubtitle={(day) => {
          const totals = countTotalsOfDay(items, day, totalsTeacherFilter);
          if (totals.classRoomCount === 0 && totals.availableCount === 0)
            return undefined;
          return (
            <span className={"flex flex-col leading-tight tabular-nums"}>
              <span>{totals.classRoomCount} classrooms</span>
              {!loadingAvailability && (
                <span>{totals.availableCount} available</span>
              )}
            </span>
          );
        }}
        itemRenderer={(group) => (
          <BookedClassRoomSlotItem
            group={group}
            onClick={() => setSelectedGroup(group)}
            selectedTeacherId={selectedTeacherId}
            candidateClassRoomIds={candidateClassRoomIds}
          />
        )}
        emptyDayComponent={
          <p className={"text-center text-slate-400 text-sm"}>
            {loadingAvailability
              ? "Loading availability…"
              : "No classrooms nor availability"}
          </p>
        }
        rightControls={
          <div className={"w-56"}>
            <Select
              options={teacherOptions}
              value={selectedTeacherId ?? undefined}
              onChange={(v) => setSelectedTeacherId(v)}
              placeholder={"Filter by teacher…"}
              searchable
              clearable
            />
          </div>
        }
      />

      <BookedClassRoomsLegend
        isTeacherFilterActive={!!selectedTeacherId}
        loadingCandidates={loadingCandidates}
        loadingAvailability={loadingAvailability}
      />

      <ExportBookedClassRoomsCsvModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        defaultInterval={interval}
        defaultTeacherId={selectedTeacherId}
        teacherOptions={teacherOptions}
      />

      {selectedGroup && (
        <SlotDetailPanel
          group={selectedGroup}
          teacherById={teacherById}
          onClose={() => setSelectedGroup(null)}
          selectedTeacherId={selectedTeacherId}
          candidateClassRoomIds={candidateClassRoomIds}
        />
      )}
    </Layout>
  );
}
